import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { formatCurrency, CHART_COLORS, getShortMonthName } from '../lib/utils';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-white/10 rounded-lg px-3 py-2 shadow-lg">
        <p className="text-sm font-medium">{payload[0].name || payload[0].payload.name}</p>
        <p className="text-sm text-primary tabular-nums">{formatCurrency(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

export const DonutChart = ({ data, total, topCategory }) => {
  const chartData = Object.entries(data || {}).map(([name, info], index) => ({
    name,
    value: info.total,
    color: CHART_COLORS[index % CHART_COLORS.length],
  }));

  if (chartData.length === 0) {
    return (
      <Card className="bg-card border-white/10" data-testid="donut-chart-empty">
        <CardContent className="pt-6 flex items-center justify-center h-[300px]">
          <p className="text-muted-foreground">No expenses to display</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-white/10" data-testid="donut-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground tracking-wide uppercase">
          Total Balance
        </CardTitle>
        <p className="text-4xl font-bold tracking-tight tabular-nums balance-amount" data-testid="total-balance">
          {formatCurrency(total)}
        </p>
      </CardHeader>
      <CardContent>
        <div className="relative h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                stroke="transparent"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          {topCategory && (
            <div className="donut-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Top</p>
              <p className="text-lg font-semibold">{topCategory}</p>
            </div>
          )}
        </div>
        
        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-4 mt-4">
          {chartData.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-sm text-muted-foreground">{entry.name}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export const YearlyBarChart = ({ data, total, year }) => {
  // Prepare data for all 12 months
  const monthlyData = [];
  for (let i = 1; i <= 12; i++) {
    const monthKey = `${year}-${String(i).padStart(2, '0')}`;
    monthlyData.push({
      month: getShortMonthName(i),
      value: data?.[monthKey] || 0,
    });
  }

  return (
    <Card className="bg-card border-white/10" data-testid="yearly-bar-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground tracking-wide uppercase">
          Total Balance
        </CardTitle>
        <p className="text-4xl font-bold tracking-tight tabular-nums balance-amount" data-testid="yearly-total">
          {formatCurrency(total)}
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[200px] mt-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData}>
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#71717A', fontSize: 10 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#71717A', fontSize: 10 }}
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="value"
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-center text-sm text-muted-foreground mt-2">Total vs. Month</p>
      </CardContent>
    </Card>
  );
};
